import { uuid } from './uuid.js';
import { consentStatus } from './consent.js';

/**
 * Every event carries the same context block inside `properties`.
 *
 * The pixel SDK forwards `properties` untouched and the ingest service stores it as
 * ClickHouse JSON, so this is the only place where cross-event context can live.
 * Keep each key's type stable across events — the column type is inferred once.
 */

// Must match the `Version:` header in trackdaddy-tracker.php — the PHP half reads that
// header into TRACKDADDY_TRACKER_VERSION, and a downstream query cannot version-slice a
// visit whose two halves disagree.
export const TRACKER_VERSION = '2.0.3';

export function createEnvelope({ context, session, win = typeof window !== 'undefined' ? window : {} }) {
  return function envelope(name, params = {}, options = {}) {
    const state = session.touch();

    const properties = {
      event_id: uuid(),
      session_id: state.id,
      // The manager owns the clock; asking it keeps the age consistent with the session
      // window that decided whether this is still the same session.
      session_age_s:
        typeof session.ageSeconds === 'function'
          ? session.ageSeconds()
          : Math.max(0, Math.round((Date.now() - state.started) / 1000)),
      page_type: context.page_type,
      page_family: context.page_family,
      page_path: context.page_path,
      store_id: context.store_id,
      env: context.env,
      consent_status: consentStatus(win),
      tracker_version: TRACKER_VERSION,
      client_ts: new Date().toISOString(),
      // Which half of the plugin sent this. The server sets `source: 'server'` in
      // EnvelopeFactory, and the dedup check in docs/TESTING.md groups on it to tell the
      // two deliveries of a dual-emitted event apart — which only works if both sides
      // set it.
      source: 'browser',
    };

    if (context.landing_id) {
      properties.landing_id = context.landing_id;
    }

    // Two different keys live in `options.dedupeKey`, and they must not be confused.
    //
    // Locally it is the suppression key for `once`, which is per-browser, so a constant
    // like 'begin_checkout' is fine there. On the wire it is `dedupe_key`, which groups
    // rows across every visitor of every store — a constant there means one row per key
    // for the whole estate, so the duplicate_event_fired alert is permanently red and
    // the collapse advised in docs/DOWNSTREAM-QUERIES.md would delete real traffic.
    //
    // So the emitted key is session-scoped unless `shared` marks it as one that has to
    // match the server copy byte for byte (purchase).
    properties.dedupe_key = options.dedupeKey
      ? options.shared
        ? options.dedupeKey
        : options.dedupeKey + ':' + state.id
      : name + ':' + state.id + ':' + properties.event_id;

    return Object.assign(properties, sanitize(params));
  };
}

/**
 * Identity, consent and provenance are the envelope's to set. page_type, page_family
 * and page_path stay overridable — a collector reporting about another page is a
 * legitimate case (api_request_failed, pixel_load_failed).
 */
const RESERVED = [
  'event_id',
  'session_id',
  'session_age_s',
  'dedupe_key',
  'consent_status',
  'tracker_version',
  'client_ts',
  'store_id',
  'env',
  'source',
];

/**
 * Drops undefined/null values so an absent parameter never lands as a typed null in
 * ClickHouse, and never lets a caller overwrite the envelope's own keys.
 */
function sanitize(params) {
  const out = {};

  Object.keys(params || {}).forEach((key) => {
    const value = params[key];

    if (value === undefined || value === null) {
      return;
    }

    if (RESERVED.indexOf(key) !== -1) {
      return;
    }

    out[key] = value;
  });

  return out;
}
